import React from 'react';
import { Flame, Target, TrendingUp, Calendar, Award, Smartphone, Bell, Zap, Download } from 'lucide-react';
import KoruLogo from './KoruLogo';
import { Button } from './ui/Button';
import { ThemeToggle } from './ui/ThemeToggle';

interface LandingPageProps {
  onLogin: () => void;
  onRegister: () => void;
  onPrivacyPolicy?: () => void;
  onTermsOfService?: () => void;
  onDeleteAccount?: () => void;
  onContactSupport?: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onRegister, onPrivacyPolicy, onTermsOfService, onDeleteAccount, onContactSupport }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-primary-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Theme Toggle - Fixed Position */}
      <div className="fixed top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {/* Hero Section */}
      <div className="relative overflow-hidden pb-16 lg:pb-0">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                {/* Logo and Brand */}
                <div className="flex items-center justify-center lg:justify-start mb-8">
                  <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg">
                    <KoruLogo size={64} />
                  </div>
                  <div className="ml-4">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                      Koru
                    </h1>
                    <p className="text-sm text-primary-600 dark:text-primary-400 font-medium">
                      Habit Tracker
                    </p>
                  </div>
                </div>

                {/* Hero Text */}
                <h2 className="text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white sm:text-5xl md:text-6xl">
                  <span className="block">Build Better</span>
                  <span className="block text-primary-600 dark:text-primary-400">
                    Habits Daily
                  </span>
                </h2>
                <p className="mt-3 text-base text-gray-500 dark:text-gray-300 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Transform your life one habit at a time. Track your progress, maintain streaks, and achieve your goals with our beautiful and intuitive habit tracker.
                </p>

                {/* CTA Buttons */}
                <div className="mt-8 sm:mt-10 sm:flex sm:justify-center lg:justify-start">
                  <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex">
                    <Button
                      onClick={onRegister}
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      Get Started Free
                    </Button>
                    <Button
                      onClick={onLogin}
                      variant="outline"
                      size="lg"
                      className="w-full sm:w-auto"
                    >
                      Sign In
                    </Button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>

        {/* Hero Image/Illustration */}
        <div className="mt-16 lg:mt-0 lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <div className="h-64 w-full sm:h-80 md:h-96 lg:w-full lg:h-full flex items-center justify-center px-4">
            <div className="relative">
              {/* Phone Frame with Real Screenshot */}
              <div className="w-48 h-96 sm:w-56 sm:h-[448px] md:w-64 md:h-[520px] bg-gray-900 rounded-[2.5rem] sm:rounded-[3rem] p-1.5 sm:p-2 shadow-2xl">
                <div className="w-full h-full rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden relative">
                  <img 
                    src="/images/android/dashboard-michael.png" 
                    alt="Koru Android App Dashboard"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Floating Elements - Hidden on mobile to reduce clutter */}
              <div className="hidden sm:block absolute -top-4 -left-4 w-6 h-6 md:w-8 md:h-8 bg-primary-500 rounded-full animate-bounce"></div>
              <div className="hidden sm:block absolute -bottom-4 -right-4 w-4 h-4 md:w-6 md:h-6 bg-green-500 rounded-full animate-pulse"></div>
              <div className="hidden sm:block absolute top-1/2 -left-6 md:-left-8 w-3 h-3 md:w-4 md:h-4 bg-orange-500 rounded-full animate-ping"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Announcement Banner */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-green-500/20 rounded-full text-white text-sm font-medium mb-6 border border-green-400/30">
              <Download className="w-4 h-4 mr-2" />
              Now Available on iOS & Android
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
              Track Habits on the Go
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Download our native app today and build better habits anywhere, anytime.
              Available on iOS and Android, built with React Native and Expo for the ultimate mobile experience.
            </p>
            
            {/* Platform Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Bell className="w-6 h-6 text-primary-100" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Smart Notifications</h3>
                <p className="text-primary-200 text-sm">Never miss a habit with intelligent push notifications and reminders</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Zap className="w-6 h-6 text-primary-100" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Lightning Fast</h3>
                <p className="text-primary-200 text-sm">Native performance with instant habit tracking and real-time sync</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <Smartphone className="w-6 h-6 text-primary-100" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Mobile First</h3>
                <p className="text-primary-200 text-sm">Designed specifically for mobile with intuitive gestures and navigation</p>
              </div>
            </div>

            {/* Store Download Badges */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              {/* iOS App Store - Live */}
              <a
                href="https://apps.apple.com/us/app/koru-mobile/id6753133508"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-black hover:bg-gray-900 rounded-lg px-6 py-3 backdrop-blur-sm border border-white/20 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <div className="w-8 h-8 bg-white dark:bg-gray-100 rounded-md flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-gray-900" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.13997 6.91 8.85997 6.88C10.15 6.85 11.36 7.72 12.11 7.72C12.86 7.72 14.3 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-white text-sm font-medium">Download on the</div>
                  <div className="text-gray-300 text-xs">App Store</div>
                </div>
              </a>
              
              {/* Google Play Store - Live Download Link */}
              <a 
                href="https://play.google.com/store/apps/details?id=com.koru.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-black hover:bg-gray-900 rounded-lg px-6 py-3 backdrop-blur-sm border border-white/20 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <div className="w-8 h-8 bg-white dark:bg-gray-100 rounded-md flex items-center justify-center mr-3">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                    <path d="M3.609 1.814L13.792 12.001L3.61 22.186C3.63 22.186 3.65 22.185 3.67 22.185C3.97 22.185 4.28 22.067 4.54 21.832L15.175 15.993L13.792 12.001L4.54 2.169C4.28 1.934 3.97 1.816 3.67 1.816C3.65 1.816 3.63 1.815 3.61 1.815L3.609 1.814Z" fill="#32BBFF"/>
                    <path d="M20.485 10.18L15.175 7.99L13.792 12.001L15.175 15.993L20.485 13.804C21.205 13.444 21.205 12.541 20.485 10.18Z" fill="#32BBFF"/>
                    <path d="M15.175 8.004L4.54 2.169C4.28 1.934 3.97 1.816 3.67 1.816C3.65 1.816 3.63 1.815 3.61 1.815L13.792 12.001L15.175 8.004Z" fill="#229954"/>
                    <path d="M13.792 12.001L3.61 22.186C3.63 22.186 3.65 22.185 3.67 22.185C3.97 22.185 4.28 22.067 4.54 21.832L15.175 15.993L13.792 12.001Z" fill="#E74C3C"/>
                    <path d="M13.792 12.001L15.175 15.993L20.485 13.804C21.205 13.444 21.205 12.541 20.485 10.18L15.175 8.004L13.792 12.001Z" fill="#F39C12"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="text-white text-sm font-medium">Download Now</div>
                  <div className="text-gray-300 text-xs">Google Play</div>
                </div>
              </a>
            </div>

            <p className="text-primary-100 text-sm">
              Available on iOS and Android
            </p>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 dark:text-primary-400 font-semibold tracking-wide uppercase">
              Features
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Everything you need to succeed
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 dark:text-gray-300 lg:mx-auto">
              Powerful features designed to help you build lasting habits and achieve your goals.
            </p>
          </div>

          <div className="mt-16">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {/* Feature 1 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <Flame className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Streak Tracking
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Visualize your progress with powerful streak tracking that motivates you to keep going.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <Calendar className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Flexible Scheduling
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Set up habits with daily, weekly, or custom frequencies that fit your lifestyle.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Progress Analytics
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Get insights into your habits with detailed analytics and progress charts.
                </p>
              </div>

              {/* Feature 4 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <Award className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Achievements
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Celebrate milestones and unlock achievements as you build consistent habits.
                </p>
              </div>

              {/* Feature 5 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <Smartphone className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Native Mobile Apps
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Android app available now on Google Play. iOS coming soon. Full offline support and smart push notifications.
                </p>
              </div>

              {/* Feature 6 */}
              <div className="text-center">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white mx-auto">
                  <Target className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-lg leading-6 font-medium text-gray-900 dark:text-white">
                  Goal Setting
                </h3>
                <p className="mt-2 text-base text-gray-500 dark:text-gray-300">
                  Set meaningful goals and track your progress towards achieving them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile App Showcase Section */}
      <div className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              Android App Features
            </h2>
            <p className="mt-4 text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Our Android app is live on Google Play. Built with React Native and Expo, it delivers 
              native performance with all the features you love from the web app, plus mobile-exclusive benefits.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Mobile Features List */}
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Smart Push Notifications
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Get personalized reminders at the perfect time. Never miss a habit with intelligent notifications 
                    that adapt to your schedule and preferences.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Instant Habit Tracking
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Mark habits complete with a single tap. Native performance ensures your progress 
                    is saved instantly, even with poor network connectivity.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Offline Access
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Track your habits anywhere, anytime. Your data syncs automatically when 
                    you're back online, ensuring you never lose progress.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Native Calendar Integration
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Visualize your habit streaks with beautiful calendar views optimized for mobile. 
                    Swipe through months and see your progress at a glance.
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile App Mockup */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                {/* Phone Frame with Real Screenshot */}
                <div className="w-64 h-[520px] bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
                  <div className="w-full h-full rounded-[2rem] overflow-hidden relative">
                    <img 
                      src="/images/android/habits-detail.png" 
                      alt="Koru Android App - Habits List"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="mt-16 text-center">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Built with Modern Technology
            </h3>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                React Native
              </span>
              <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                Expo SDK 54+
              </span>
              <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                TypeScript
              </span>
              <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                Native Performance
              </span>
              <span className="px-3 py-1 bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                Cross-Platform
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-600 dark:bg-primary-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to build better habits?</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-primary-100">
            Start tracking habits today on web or download our app from Google Play or the App Store.
          </p>
          <div className="mt-8 flex flex-col items-center gap-4">
            {/* Web CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={onRegister}
                variant="secondary"
                size="lg"
                className="w-full sm:w-auto"
              >
                Start Your Journey
              </Button>
              <button
                onClick={onLogin}
                className="inline-flex items-center justify-center h-12 px-6 text-base font-medium rounded-lg border-2 border-white text-white bg-transparent hover:bg-white hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                Sign In
              </button>
            </div>
            {/* App download links */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://play.google.com/store/apps/details?id=com.koru.mobile"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-12 px-6 text-base font-medium rounded-lg border-2 border-white text-white bg-transparent hover:bg-white hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <Download className="w-5 h-5 mr-2" />
                Get Android App
              </a>
              <a
                href="https://apps.apple.com/us/app/koru-mobile/id6753133508"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center h-12 px-6 text-base font-medium rounded-lg border-2 border-white text-white bg-transparent hover:bg-white hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <Download className="w-5 h-5 mr-2" />
                Get iOS App
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                Koru
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              © {new Date().getFullYear()} Koru. All rights reserved. Build better habits, one day at a time.
            </p>
            {(onPrivacyPolicy || onTermsOfService || onContactSupport || onDeleteAccount) && (
              <div className="mt-2 flex items-center justify-center flex-wrap gap-x-4 gap-y-1">
                {onPrivacyPolicy && (
                  <button
                    onClick={onPrivacyPolicy}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline transition-colors"
                  >
                    Privacy Policy
                  </button>
                )}
                {onPrivacyPolicy && onTermsOfService && (
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                )}
                {onTermsOfService && (
                  <button
                    onClick={onTermsOfService}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline transition-colors"
                  >
                    Terms of Service
                  </button>
                )}
                {onTermsOfService && onContactSupport && (
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                )}
                {onContactSupport && (
                  <button
                    onClick={onContactSupport}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline transition-colors"
                  >
                    Contact Support
                  </button>
                )}
                {onContactSupport && onDeleteAccount && (
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                )}
                {onDeleteAccount && (
                  <button
                    onClick={onDeleteAccount}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:underline transition-colors"
                  >
                    Delete Account
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 